import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  useChannelActionContext,
  useChannelStateContext,
} from 'stream-chat-react';
import styled from 'styled-components';
import { format } from 'date-fns';
import {
  Avatar,
  Button,
  Empty,
  Icon,
  message,
  Text,
  theme,
  Title,
} from 'components';
import { ContextualChatContext, RightSidePanelPageType } from 'contexts';
import { Loading } from '@app/react-common';
import { useBreakpoint } from 'hooks';
import SearchInputBase from './SearchInputBase';

const MESSAGES_LIMIT = 100;

interface ISideSearchInChatPanel {
  setHighlightedId: Dispatch<SetStateAction<string | null>>;
}

const SideSearchInChatPanel = ({
  setHighlightedId,
}: ISideSearchInChatPanel) => {
  const { client, setRightSidePanelPage } = useContext(ContextualChatContext);
  const { channel } = useChannelStateContext();
  const { jumpToMessage } = useChannelActionContext();
  const { lg, md } = useBreakpoint();

  const [messagesQuery, setMessagesQuery] = useState<string>('');

  const {
    data,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError,
    refetch,
  } = useInfiniteQuery({
    enabled: !!messagesQuery && !!channel?.cid,
    initialData: { pages: [], pageParams: [] },
    queryKey: ['contextualChat', channel?.cid, messagesQuery],
    queryFn: async ({ pageParam = 0 }) => {
      const queryResults = await client.search(
        { cid: channel.cid },
        messagesQuery
          ? { text: { $autocomplete: messagesQuery } }
          : { type: 'regular' },
        { limit: MESSAGES_LIMIT, offset: pageParam },
      );
      return queryResults.results.map(item => item.message);
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.flat()?.length;
      return lastPage?.length === MESSAGES_LIMIT ? totalFetched : undefined;
    },
  });

  const messages = useMemo(
    () => data?.pages?.flatMap(page => page || []) ?? [],
    [data?.pages],
  );

  const highlightText = (text: string) => {
    const regex = new RegExp(
      `(${messagesQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
      'gi',
    );
    return text.split(regex);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (
      scrollHeight - scrollTop <= clientHeight + 50 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  };

  const handleResultClick = async (messageId?: string) => {
    if (messageId) {
      await channel.state.loadMessageIntoState(messageId).catch(() => {
        message.error('Unable to find message');
      });
      await jumpToMessage(messageId);

      setHighlightedId(messageId);
    }
  };

  const maxWidth = useMemo(
    () => (lg ? '300px' : `calc(100dvw - ${md ? 462 : 0}px)`),
    [lg, md],
  );

  return (
    <StyledDetailsContainer maxWidth={maxWidth}>
      <StyledPrimaryHeader>
        <Icon
          type="antdArrowLeftOutlined"
          width={18}
          color="primary"
          onClick={() => {
            setRightSidePanelPage(RightSidePanelPageType.ChatDetails);
          }}
        />
        <Title level={3} style={{ margin: '25px 0px' }}>
          Search chat
        </Title>
      </StyledPrimaryHeader>

      <SearchInputBase
        inputProps={{ autoFocus: true }}
        onSearch={query => setMessagesQuery(query)}
      />

      <StyledResultContainer>
        {isFetching && !isFetchingNextPage ? (
          <Loading />
        ) : (
          <>
            {isError ? (
              <RetryContainer>
                <Text style={{ textAlign: 'center' }}>
                  Sorry, we were unable to get your messages.
                </Text>
                <Button onClick={() => refetch()}>Retry</Button>
              </RetryContainer>
            ) : (
              <>
                {!messages?.length || !messagesQuery ? (
                  <Empty
                    description="No results found"
                    style={{ marginTop: 24 }}
                  />
                ) : (
                  <StyledMessagesContainer onScroll={handleScroll}>
                    {messages.map((msg, idx) => {
                      const names = msg.user?.name?.split(' ');
                      const date = format(new Date(msg.created_at), 'L/d');
                      const isLast = idx === messages.length - 1;
                      const highlightedText = highlightText(msg.text);

                      return (
                        <StyledMessageResult
                          onClick={() => handleResultClick(msg.id)}
                          key={`message-result-${idx}`}
                          $isLast={isLast}
                        >
                          <Avatar
                            user={{
                              firstName: names?.at(0) ?? 'U',
                              lastName: names?.at(1) ?? 'U',
                            }}
                          />
                          <StyledTextContainer>
                            <StyledTopContainer>
                              <Text
                                color={theme.textColorSecondary}
                                fontSize={13}
                              >
                                {msg.user?.name}
                              </Text>
                              <Text
                                color={theme.textColorSecondary}
                                fontSize={13}
                              >
                                {date}
                              </Text>
                            </StyledTopContainer>
                            <StyledMessageText>
                              {highlightedText.map((text, index) => {
                                const isHighlight =
                                  text.toLowerCase() ===
                                  messagesQuery.toLowerCase();

                                return (
                                  <StyledHighlight
                                    isHighlight={isHighlight}
                                    key={`match-${text}-${index}`}
                                  >
                                    {text}
                                  </StyledHighlight>
                                );
                              })}
                            </StyledMessageText>
                          </StyledTextContainer>
                        </StyledMessageResult>
                      );
                    })}
                    {isFetchingNextPage && <Loading />}
                  </StyledMessagesContainer>
                )}
              </>
            )}
          </>
        )}
      </StyledResultContainer>
    </StyledDetailsContainer>
  );
};

const RetryContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex-direction: column;
  flex: 1;
  margin-top: 15px;
`;

const StyledHighlight = styled.span<{ isHighlight?: boolean }>`
  background-color: ${({ isHighlight }) =>
    isHighlight ? '#ffedbf' : 'transparent'};
  font-weight: ${({ isHighlight }) => (isHighlight ? 600 : 'normal')};
  white-space: normal;
`;

const StyledMessageText = styled.div`
  display: block;
  word-break: break-word;
  overflow-wrap: anywhere;
`;

const StyledTopContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const StyledMessagesContainer = styled.div`
  overflow: auto;
  height: 100%;
`;

const StyledResultContainer = styled.div`
  height: calc(100% - 132px);
`;

const StyledMessageResult = styled.div<{ $isLast: boolean }>`
  display: flex;
  gap: 8px;
  border-bottom: ${({ $isLast }) =>
    $isLast ? undefined : `0.5px solid ${theme.dividerColor}`};
  padding: 8px;
  border-radius: 10px;
  &:hover {
    cursor: pointer;
    background-color: #f0f7ff;
  }
`;

const StyledDetailsContainer = styled.div<{ maxWidth: string }>`
  flex: 1;
  min-width: 300px;
  max-width: ${({ maxWidth }) => maxWidth};
  height: 100%;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
  padding: 0px 16px;
  border-left: 1px solid #e3e3e3;
  animation: slideInRight 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  @keyframes slideInRight {
    from {
      transform: translateX(-5%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const StyledPrimaryHeader = styled.div`
  display: flex;
  justify-content: left;
  align-items: center;
  gap: 12px;
`;

export default SideSearchInChatPanel;
